'use strict';

const SAMPLE_PREVIEW_SLUGS = ['achievers', 'workday'];
const SAMPLE_PREVIEW_MODES = ['add', 'edit'];
const SAMPLE_PREVIEW_NOTICE =
    'Sample data is being used for this preview. Changes to preview data will not be saved.';
const SPOTLIGHT_FALLBACK_NOTICE =
    'No roles marked Spotlight were found. Showing roles using the standard selection instead.';

function canUseSamplePreview({ enabled, mode, slug } = {}) {
    return !!enabled && SAMPLE_PREVIEW_MODES.includes(mode) && SAMPLE_PREVIEW_SLUGS.includes(slug);
}

function dateAtOffset(now, offsetDays) {
    const date = new Date(now instanceof Date ? now.getTime() : now || Date.now());
    date.setUTCDate(date.getUTCDate() + offsetDays);
    return date;
}

function timestampAtOffset(now, offsetDays) {
    return dateAtOffset(now, offsetDays).toISOString();
}

function dateOnlyAtOffset(now, offsetDays) {
    return timestampAtOffset(now, offsetDays).slice(0, 10);
}

function samplePeople() {
    // A deliberately varied fictional cast: short and very long names, apostrophes,
    // hyphens, ampersands and diacritics, plus one entry with a missing image so
    // the preview exercises the no-avatar path.
    return [
        { displayName: 'Alex Morgan', imageUrl: 'https://images.example.invalid/alex.png' },
        { displayName: 'Sam Rivera', imageUrl: 'https://images.example.invalid/sam.png' },
        { displayName: 'Jordan Lee', imageUrl: 'https://images.example.invalid/jordan.png' },
        { displayName: 'Taylor Kim', imageUrl: 'https://images.example.invalid/taylor.png' },
        {
            displayName: 'Aoife Ní Bhraonáin-Šulcová',
            imageUrl: 'https://images.example.invalid/aoife.png',
        },
        { displayName: "Jean-Luc O'Callaghan", imageUrl: 'https://images.example.invalid/jeanluc.png' },
        {
            displayName: 'Wolfgang von Habsburg-Lothringen the Third',
            imageUrl: 'https://images.example.invalid/wolfgang.png',
        },
        { displayName: 'Bo', imageUrl: 'https://images.example.invalid/bo.png' },
        // Missing image on purpose.
        { displayName: 'Priya Raghunathan-Venkataraman' },
        { displayName: 'Mac & Cheese Team', imageUrl: 'https://images.example.invalid/team.png' },
    ];
}

// A message just over the 250-character clamp/skip threshold, and a very short one.
const LONG_RECOGNITION_MESSAGE =
    'Across an exceptionally demanding quarter you steadied the whole programme: you unblocked three teams at once, '
    + 'rewrote the onboarding guide from scratch, mentored two new joiners through their first release, and still found '
    + 'time to make everyone feel genuinely welcome and supported every single day.';
const SHORT_RECOGNITION_MESSAGE = 'Legend. Thank you!';

function recognitionFixture(now) {
    const people = samplePeople();
    // Column order: [key, offsetDays, criterion, message, points, nominator, nominees].
    // Covers the four branded criteria (ambitious/collaborative/curious/for the
    // customer), unknown criteria (default variant), a missing/blank criterion,
    // single vs multiple nominees, a missing nominator, and short/long/near-limit
    // copy so the preview shows the real text/CSS variations.
    const definitions = [
        ['1', -1, 'Collaborative', 'Thank you for bringing the whole team together.', 50, people[1], [people[0]]],
        ['2', -2, 'Curious', 'Your thoughtful questions helped us find a better answer.', 25, people[3], [people[2]]],
        ['3', -3, 'Ambitious', LONG_RECOGNITION_MESSAGE, 100, people[0], [people[3]]],
        ['4', -4, 'For the Customer', 'You put the customer first, every time.', 40, people[5], [people[4]]],
        ['5', -6, 'Teamwork', 'You made a new colleague feel genuinely welcome.', 10, people[2], [people[1], people[7]]],
        ['6', -8, 'Ownership', SHORT_RECOGNITION_MESSAGE, 15, people[4], [people[6]]],
        ['7', -12, 'Innovation', 'Your prototype unlocked a whole new approach for the team.', 75, people[6], [people[8], people[0], people[3]]],
        // Missing nominator (recognised anonymously) + missing criterion.
        ['8', -20, '', 'Quietly brilliant work behind the scenes.', 5, null, [people[9]]],
        ['9', -30, 'Kindness', 'You made everyone’s week a little brighter.', 20, people[8], [people[5]]],
    ];
    return {
        sourceType: 'achievers.recognitions',
        items: definitions.map(([id, offset, criterion, message, pointsAwarded, nominator, nominees]) => {
            const item = {
                sourceRecordKey: `sample-recognition-${id}`,
                recognizedAt: timestampAtOffset(now, offset),
                criterion,
                message,
                pointsAwarded,
                nominees,
            };
            if (nominator) item.nominator = nominator;
            return item;
        }),
    };
}

function newStarterFixture(now) {
    const people = samplePeople();
    // Enough starters (with long and short names) to fill multiple pages.
    const offsets = [-1, -3, -5, -7, -10, -14, -21, -30, -45, -60];
    return {
        sourceType: 'achievers.newsfeed.new_starter',
        items: people.map((person, index) => ({
            sourceRecordKey: `sample-new-starter-${index + 1}`,
            eventType: 'activation',
            occurredAt: timestampAtOffset(now, offsets[index % offsets.length]),
            title: `Welcome ${person.displayName}`,
            person,
        })),
    };
}

function serviceAnniversaryFixture(now) {
    const people = samplePeople();
    // Milestones from a first year through 30+ years, with long names, so both the
    // single and multiple (table) Long Service layouts get exercised.
    const years = [1, 3, 5, 10, 15, 20, 25, 30, 35, 40];
    const offsets = [-1, -4, -6, -9, -12, -16, -22, -28, -34, -45];
    return {
        sourceType: 'achievers.newsfeed.service_anniversary',
        items: people.map((person, index) => ({
            sourceRecordKey: `sample-service-anniversary-${index + 1}`,
            eventType: 'celebration',
            occurredAt: timestampAtOffset(now, offsets[index % offsets.length]),
            title: `Celebrating ${years[index % years.length]} Years of Service!`,
            yearsOfService: years[index % years.length],
            person,
        })),
    };
}

function achieversFixture(appData, now) {
    const whatToPlay = appData && appData.whatToPlay;
    if (whatToPlay === 'new-starters') return newStarterFixture(now);
    if (whatToPlay === 'long-service-single' || whatToPlay === 'long-service-multiple') {
        return serviceAnniversaryFixture(now);
    }
    return recognitionFixture(now);
}

function workdayFixture(now) {
    const definitions = [
        // spotlight column: true here on VISIBLE (non-confidential) roles so the
        // Spotlight layout and Include/Only/Exclude controls are actually testable.
        // sample-role-4 keeps spotlight+confidential to exercise the confidentiality-
        // first fallback (a marked role that must NOT surface when confidential).
        ['sample-role-1', 'People Operations Partner', 'London', -1, true, false],
        ['sample-role-2', 'Product Designer', 'Warsaw', -3, true, false],
        ['sample-role-3', 'Customer Success Lead', 'Bristol', null, null, false],
        ['sample-role-4', 'Engineering Manager', 'Remote', -15, true, true],
        ['sample-role-5', 'Finance Analyst', 'Manchester', -35, false, false],
        ['sample-role-6', 'Workplace Coordinator', 'Krakow', -3, null, false],
        ['sample-role-7', 'Data Analyst', 'Leeds', -2, false, false],
        [
            'sample-role-8',
            'Partnership Business Development Manager (B2B Retirement Partnerships) – Example Financial Solutions',
            'London',
            -4,
            false,
            false,
        ],
        [
            'sample-role-9',
            'Head of Strategic Partnerships and Institutional Distribution (UK & Ireland Markets)',
            'Edinburgh',
            -5,
            null,
            false,
        ],
        ['sample-role-10', 'IT Service Desk Analyst (12-month FTC)', 'Bristol', -6, false, false],
        ['sample-role-11', 'Reward & Benefits Partner – UK and Ireland', 'Reigate', -7, null, false],
        ['sample-role-12', 'Group Risk, Compliance & Governance Director', 'London', -8, false, false],
        [
            'sample-role-13',
            'B2B Retirement Partnerships Manager / Example Financial Solutions',
            'Remote',
            -10,
            false,
            false,
        ],
        ['sample-role-14', 'Early Careers Programme Coordinator', 'Warsaw', -20, null, false],
        [
            'sample-role-15',
            'Lead Site Reliability Engineer (Cloud Platform & Developer Experience)',
            'Remote',
            -45,
            false,
            false,
        ],
        [
            'sample-role-16',
            'Procurement Category Manager - Technology, Data and Professional Services',
            'Manchester',
            -60,
            false,
            false,
        ],
    ];
    const jobs = definitions.map(([id, title, location, offset, spotlight, confidential], index) => {
        const dates = offset === null
            ? { closingDate: dateOnlyAtOffset(now, 30) }
            : {
                postedDate: dateOnlyAtOffset(now, offset),
                closingDate: dateOnlyAtOffset(now, offset + 45),
            };
        return {
            id,
            title,
            siteId: 'sample-careers',
            location,
            ...dates,
            remoteType: location === 'Remote' ? 'Remote' : index % 2 ? 'Hybrid' : 'On site',
            department: index % 2 ? 'Operations' : 'People',
            jobFamily: 'Sample roles',
            timeType: 'Full time',
            descriptionHtml: '<p>This is fictional sample data for preview.</p>',
            requisitionId: `SAMPLE-${index + 1}`,
            confidential,
            spotlight,
            url: `https://jobs.example.invalid/apply/${id}`,
        };
    });

    return {
        totalJobs: jobs.length,
        jobs,
        source: 'workday',
        tenant: 'sample-tenant',
        truncated: false,
    };
}

function buildSamplePreviewData(slug, appData, now) {
    if (slug === 'achievers') return achieversFixture(appData || {}, now);
    if (slug === 'workday') return workdayFixture(now);
    return null;
}

function getWorkdaySpotlightFallbackNotice(appData, body) {
    // Post-E2E Fix 3: the fallback notice fires only for the "Only" Spotlight
    // filter. Include and Exclude never surface a "no Spotlight roles" notice.
    if (!appData || appData.spotlightFilter !== 'only') return null;
    if (!body || !Array.isArray(body.jobs)) return null;
    const jobs = body.jobs;
    const visibleJobs = appData.includeConfidential === true
        ? jobs
        : jobs.filter((job) => !job.confidential);
    return visibleJobs.some((job) => job.spotlight === true)
        ? null
        : SPOTLIGHT_FALLBACK_NOTICE;
}

function getPreviewNoticeBody({ isSample, slug, appData, now, dynamic, cache } = {}) {
    if (isSample) return buildSamplePreviewData(slug, appData || {}, now);
    if (slug === 'workday' && (!dynamic || !Array.isArray(dynamic.jobs))) return cache;
    return dynamic;
}

function getSamplePreviewNotice({ isSample, slug, appData, body } = {}) {
    const fallbackNotice = slug === 'workday'
        ? getWorkdaySpotlightFallbackNotice(appData, body)
        : null;
    if (!isSample) return fallbackNotice;
    return fallbackNotice
        ? `${SAMPLE_PREVIEW_NOTICE} ${fallbackNotice}`
        : SAMPLE_PREVIEW_NOTICE;
}

function createPreviewDataSource(initialLiveData) {
    let source = 'live';
    let liveData = initialLiveData;
    let sampleData = null;
    let sampleClock = null;

    return {
        acceptLive(body) {
            liveData = body;
            return this.current();
        },
        useSample(slug, appData, now) {
            sampleClock = sampleClock || dateAtOffset(now, 0);
            sampleData = buildSamplePreviewData(slug, appData, sampleClock);
            source = 'sample';
            return sampleData;
        },
        refreshSample(slug, appData) {
            if (source !== 'sample') return this.current();
            sampleData = buildSamplePreviewData(slug, appData, sampleClock);
            return sampleData;
        },
        useLive() {
            source = 'live';
            return liveData;
        },
        current() {
            return source === 'sample' ? sampleData : liveData;
        },
        isSample() {
            return source === 'sample';
        },
    };
}

if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = {
        SAMPLE_PREVIEW_NOTICE,
        SPOTLIGHT_FALLBACK_NOTICE,
        buildSamplePreviewData,
        canUseSamplePreview,
        createPreviewDataSource,
        getPreviewNoticeBody,
        getSamplePreviewNotice,
        getWorkdaySpotlightFallbackNotice,
    };
}
